import { useTranslation } from 'react-i18next'
import { Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { SchemaField } from '@/schemas/workflow'

const MAX_FIELDS = 50

const FIELD_TYPES = ['string', 'number', 'boolean', 'array', 'object'] as const

interface SchemaBuilderProps {
  fields: SchemaField[]
  onChange: (fields: SchemaField[]) => void
  title: string
}

// eslint-disable-next-line react-refresh/only-export-components -- helper + component live in same module by design
export function jsonSchemaToFields(schema: Record<string, unknown>): SchemaField[] {
  const props = (schema as Record<string, unknown>)?.properties as Record<string, Record<string, unknown>> | undefined
  if (!props) return []
  const required = ((schema as Record<string, unknown>)?.required as string[]) || []
  return Object.entries(props).map(([name, def]) => ({
    name,
    type: (def.type as SchemaField['type']) || 'string',
    required: required.includes(name),
    description: def.description as string | undefined,
  }))
}

// eslint-disable-next-line react-refresh/only-export-components -- helper + component live in same module by design
export function fieldsToJsonSchema(fields: SchemaField[]): Record<string, unknown> {
  const properties: Record<string, unknown> = {}
  const required: string[] = []
  for (const field of fields) {
    if (!field.name.trim()) continue
    const prop: Record<string, unknown> = { type: field.type }
    if (field.description) prop.description = field.description
    if (field.type === 'array') prop.items = { type: 'string' }
    properties[field.name] = prop
    if (field.required) required.push(field.name)
  }
  const schema: Record<string, unknown> = { type: 'object', properties }
  if (required.length > 0) schema.required = required
  return schema
}

export function SchemaBuilder({ fields, onChange, title }: SchemaBuilderProps) {
  const { t } = useTranslation('workflows')

  const handleAddField = () => {
    if (fields.length >= MAX_FIELDS) return
    onChange([...fields, { name: '', type: 'string', required: false }])
  }

  const handleRemoveField = (index: number) => {
    onChange(fields.filter((_, i) => i !== index))
  }

  const handleFieldChange = (index: number, updates: Partial<SchemaField>) => {
    const updated = fields.map((field, i) =>
      i === index ? { ...field, ...updates } : field
    )
    onChange(updated)
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-semibold text-gray-200">{title}</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1 border-gray-700/50 text-gray-300"
          onClick={handleAddField}
          disabled={fields.length >= MAX_FIELDS}
        >
          <Plus className="w-4 h-4" />
          {t('detail.schema.addField')}
        </Button>
      </div>

      {fields.length >= MAX_FIELDS && (
        <p className="text-xs text-amber-400">
          {t('detail.schema.maxFieldsReached', { max: MAX_FIELDS })}
        </p>
      )}

      {fields.length === 0 ? (
        <p className="text-sm text-gray-500 italic py-4 text-center">
          {t('detail.schema.addField')}
        </p>
      ) : (
        <div className="space-y-3">
          {/* Header row */}
          <div className="grid grid-cols-[1fr_120px_80px_1fr_40px] gap-2 items-center text-xs text-gray-500 px-1">
            <span>{t('detail.schema.fieldName')}</span>
            <span>{t('detail.schema.fieldType')}</span>
            <span className="text-center">{t('detail.schema.fieldRequired')}</span>
            <span>{t('detail.schema.fieldDescription')}</span>
            <span />
          </div>

          {fields.map((field, index) => (
            <div
              key={index}
              className="grid grid-cols-[1fr_120px_80px_1fr_40px] gap-2 items-center"
            >
              <Input
                value={field.name}
                onChange={(e) => handleFieldChange(index, { name: e.target.value })}
                placeholder={t('detail.schema.fieldName')}
                className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50 h-9 text-sm"
              />

              <Select
                value={field.type}
                onValueChange={(value) =>
                  handleFieldChange(index, { type: value as SchemaField['type'] })
                }
              >
                <SelectTrigger className="bg-white/5 border-gray-700/50 h-9 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="glass border-gray-700/50">
                  {FIELD_TYPES.map((type) => (
                    <SelectItem key={type} value={type} className="focus:bg-white/5">
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex justify-center">
                <Checkbox
                  checked={field.required}
                  onCheckedChange={(checked) =>
                    handleFieldChange(index, { required: checked === true })
                  }
                />
              </div>

              <Input
                value={field.description || ''}
                onChange={(e) =>
                  handleFieldChange(index, { description: e.target.value || undefined })
                }
                placeholder={t('detail.schema.fieldDescription')}
                className="bg-white/5 border-gray-700/50 focus:border-indigo-500/50 h-9 text-sm"
              />

              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0 text-gray-500 hover:text-red-400"
                onClick={() => handleRemoveField(index)}
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
